import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Search, Toggle, Modal, InlineNotification, Tooltip, Loading, Pagination, Dropdown } from '@carbon/react';
import { Add, Settings, TrashCan, View, CheckmarkFilled, WarningAltFilled, CloudOffline, Temperature, Humidity, BatteryFull, Time, List, Grid as GridIcon, Information, Flash, Bluetooth, Analytics, Checkmark, LightFilled, Renew, Locked } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useLiveSource } from '../hooks/useLiveSource';
import { deleteSensor } from '../services/sensorService';
import { getBatteryPercentage, formatBatteryDisplay, getBatteryColor as getBatteryColorUtil } from '../lib/batteryUtils';
import { detectDeviceType, getDeviceDisplayName } from '../utils/deviceTypeDetector';
import { getStatusColor, getStatusText } from '../utils/sensorAnalysis';
import { analyzeScreenData } from '../services/aiService';
import { useSensorContexts } from '../hooks/useSensorContexts';
import { enrichDevicesWithContexts } from '../utils/intelligenceContext';
import SensorStatusIndicator from '../components/SensorStatusIndicator';
import '../components/SensorStatusIndicator.css';

const DeviceListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { sensors, isLoading: sensorsLoading, error, refetch } = useSensorsOptimized();
  const { sensorContexts } = useSensorContexts();
  const { hasActiveSubscription, loading: subLoading } = useSubscription();
  
  // Track live data source (gateway vs BLE) for real-time icon updates
  const sensorIds = useMemo(() => sensors.map(s => s.id), [sensors]);
  const liveSources = useLiveSource(sensorIds);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'online', 'warning', 'offline'
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [sourceInfoModalOpen, setSourceInfoModalOpen] = useState(false);
  
  // Pagination state (only used for table view)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // AI Analysis state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiError, setAiError] = useState(null);
  
  // Only show loading if sensors are loading (optimized hook loads instantly from cache)
  const isLoading = sensorsLoading;

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Helper function to format temperature - wrapped in useCallback to prevent recreation on every render
  const formatTemperature = useCallback((temp, unit = 'celsius') => {
    if (temp === null || temp === undefined) return '--.-°C';
    if (unit === 'fahrenheit') {
      const fahrenheit = (temp * 9/5) + 32;
      return `${fahrenheit.toFixed(1)}°F`;
    }
    return `${temp.toFixed(1)}°C`;
  }, []); // Empty array - function doesn't depend on any state/props

  // Helper function to format last reading time (EXACTLY same as SensorPushWeb - with local time fix)
  const formatLastReading = useCallback((dateString) => {
    const date = new Date(dateString);
    if (!date || isNaN(date.getTime())) {
      return 'NO DATA';
    }
    
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).toUpperCase();
    return `${day} ${month} @ ${time}`;
  }, []);

  // Battery calculation is now handled by batteryUtils.js with device-specific formulas

  // Helper function to determine device status (consistent with React Native SensorCard)
  const getDeviceStatus = useCallback((latestReading) => {
    if (!latestReading) return 'offline';

    // Use recorded_at directly (consistent with React Native)
    // Supabase timestamps are in UTC, new Date() automatically converts to local browser time
    const date = new Date(latestReading.recorded_at);
    if (!date || isNaN(date.getTime())) return 'offline';
    
    const now = new Date();
    // Use Math.max to prevent negative values (handles future dates/timezone issues)
    const diffMs = Math.max(0, now.getTime() - date.getTime());
    const diffMins = Math.floor(diffMs / 60000);

    // Simple time-based status (same as React Native):
    // - Online (green): data within 15 minutes
    // - Warning/Stale (yellow): data between 15-60 minutes
    // - Offline (red): data older than 60 minutes
    if (diffMins <= 15) return 'online';
    if (diffMins <= 60) return 'warning';
    return 'offline';
  }, []);

  // Transform sensors data to match UI structure (like SensorPushWeb - direct approach)
  const devices = useMemo(() => {
    const transformed = sensors.map(sensor => {
      // Use enhanced device type detection
      const detectedDeviceType = detectDeviceType(sensor);
      const displayName = getDeviceDisplayName(sensor);

      // Direct approach like SensorPushWeb - use sensor.latest_reading directly
      const latestReading = sensor.latest_reading;
      
      // Format values directly from latest_reading (like SensorPushWeb)
      const temp = latestReading?.temperature ? formatTemperature(latestReading.temperature, sensor.temperature_unit) : '--.-°C';
      const humidity = latestReading?.humidity ? `${latestReading.humidity.toFixed(1)}%` : '--.--%';
      const batteryLevel = latestReading?.battery_level;
      const deviceType = detectedDeviceType || sensor.device_type || null;
      const battery = batteryLevel ? formatBatteryDisplay(batteryLevel, deviceType, true) : 'N/A';
      const batteryPct = batteryLevel ? getBatteryPercentage(batteryLevel, deviceType) : null;
      
      // Status calculation (like SensorPushWeb)
      const status = getDeviceStatus(latestReading);
      
      // Last reading time (like SensorPushWeb)
      const lastReading = latestReading?.recorded_at ? formatLastReading(latestReading.recorded_at) : 'NO DATA';
      
      // Live source tracking
      const liveSourceData = liveSources[sensor.id];
      const source = liveSourceData?.source || latestReading?.source || null;
      const isLive = liveSourceData?.isActive || false;

      return {
        id: sensor.id,
        name: displayName,
        type: sensor.device_id || 'Sensor',
        zone: sensor.zone_name || 'Unassigned',
        temp,
        humidity,
        battery,
        batteryPct,
        status,
        lastReading,
        source,
        isLive,
        deviceType: detectedDeviceType,
        // Keep existing fields for compatibility
        temperatureStatus: sensor.temperature_status,
        humidityStatus: sensor.humidity_status,
        temperatureTrend: sensor.temperature_trend,
        humidityTrend: sensor.humidity_trend,
        trendDataPoints: sensor.trend_data_points,
        sensor,
      };
    });
    return transformed;
  }, [sensors, liveSources, formatTemperature, getDeviceStatus, formatLastReading]);

  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.zone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations (only for table view)
  const totalItems = filteredDevices.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedDevices = viewMode === 'table' 
    ? filteredDevices.slice(startIndex, endIndex)
    : filteredDevices;

  // Reset to page 1 when search term changes, view mode changes, or status filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode, statusFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#24a148';
      case 'warning': return '#ff832b';
      case 'offline': return '#da1e28';
      default: return textSecondary;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return <CheckmarkFilled size={16} style={{ color: '#24a148' }} />;
      case 'warning': return <WarningAltFilled size={16} style={{ color: '#ff832b' }} />;
      case 'offline': return <CloudOffline size={16} style={{ color: '#da1e28' }} />;
      default: return null;
    }
  };

  const getBatteryColor = (batteryPct) => {
    if (batteryPct === null || batteryPct === undefined) return '#8d8d8d';
    return getBatteryColorUtil(batteryPct);
  };

  // Helper function to render trend indicator
  const renderTrendIndicator = (status, trend) => {
    if (!status || status === 'UNKNOWN') return null;
    
    const color = getStatusColor(status);
    const text = getStatusText(status);
    
    return (
      <span 
        style={{ 
          color, 
          fontSize: '0.75rem', 
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px'
        }}
      >
        {text}
      </span>
    );
  };

  // Handle delete button click
  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setDeleteModalOpen(true);
  };

  // Handle actual device deletion
  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;

    setIsDeleting(true);
    try {
      await deleteSensor(deviceToDelete.id);
      
      // Show success notification
      setNotification({
        kind: 'success',
        title: 'Device deleted',
        subtitle: `${deviceToDelete.name} has been successfully deleted.`,
      });

      // Close modal
      setDeleteModalOpen(false);
      setDeviceToDelete(null);

      // Refetch sensors to update the list
      await refetch();

      // Clear notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
    } catch (err) {
      console.error('Failed to delete device:', err);
      setNotification({
        kind: 'error',
        title: 'Delete failed',
        subtitle: err.message || 'Failed to delete device. Please try again.',
      });
      
      // Clear notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDeviceToDelete(null);
  };

  // Handle AI Analysis
  const handleAIAnalysis = async () => {
    setAiModalOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);

    try {
      // Prepare device data for AI (same shape as React Native: include raw lastReading for battery/analysis)
      const deviceData = enrichDevicesWithContexts(
        devices.map((device) => ({
          id: device.id,
          name: device.name,
          deviceType: device.deviceType,
          device_type: device.deviceType,
          status: device.status,
          temperature: device.sensor?.latest_reading?.temperature,
          humidity: device.sensor?.latest_reading?.humidity,
          battery: device.batteryPct,
          batteryLevel: device.batteryPct,
          lastReading: device.sensor?.latest_reading ?? null,
          source: device.source,
          zone: device.zone,
          temperatureStatus: device.temperatureStatus,
          humidityStatus: device.humidityStatus,
        })),
        sensorContexts,
      );

      console.log('🤖 Analyzing devices data...', {
        deviceCount: deviceData.length,
        withContext: deviceData.filter((d) => d.hasIntelligenceContext).length,
      });

      const result = await analyzeScreenData(deviceData, 'devices');
      setAiAnalysis(result);
    } catch (error) {
      console.error('❌ AI Analysis failed:', error);
      setAiError(error.message || 'Failed to analyze data. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="device-list-page">
        <div style={{ textAlign: 'center', color: textSecondary }}>
          <p>Loading devices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }} data-testid="device-list-page">
        <div style={{ textAlign: 'center', color: '#da1e28' }}>
          <p>Error loading devices: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionGate
      feature="sensor details, charts, and device management"
      disabled={!hasActiveSubscription}
      showChildrenGreyed={true}
      customMessage="You need an active subscription to access sensor details and manage your devices. Your sensor data is preserved and will be accessible once you resubscribe."
    >
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="device-list-page">
      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        danger
        modalHeading="Delete device"
        modalLabel="Confirm deletion"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={handleCancelDelete}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={isDeleting}
        size="sm"
      >
        <p style={{ marginBottom: '16px' }}>
          Are you sure you want to delete <strong>{deviceToDelete?.name}</strong>?
        </p>
        <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
          This action cannot be undone. All sensor readings and historical data for this device will be permanently deleted.
        </p>
      </Modal>

      {/* Source Information Modal */}
      <Modal
        open={sourceInfoModalOpen}
        modalHeading="Data Source Types"
        modalLabel="Information"
        passiveModal
        onRequestClose={() => setSourceInfoModalOpen(false)}
        size="md"
      >
        <div style={{ marginBottom: '24px' }}>
          <p style={{ marginBottom: '16px', color: textPrimary }}>
            Device readings can come from different sources. Here's what each tag means:
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Tag type="purple" size="sm">MQTT</Tag>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>MQTT Gateway</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Data received from your gateway via MQTT protocol. This is the most common source for gateway-connected sensors.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Tag type="cyan" size="sm">HTTP</Tag>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>HTTP Gateway</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Data received from your gateway via HTTP protocol. Alternative gateway communication method.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Tag type="blue" size="sm">APP</Tag>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>Mobile App</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Data received directly from the mobile app via Bluetooth connection. No gateway required.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Tag type="red" size="sm">LIVE</Tag>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>Live Data</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Indicates that data is actively being received from this source. The LIVE tag appears for 30 seconds after new data arrives.
                </p>
              </div>
            </div>
          </div>

          <div style={{ 
            marginTop: '24px', 
            padding: '12px', 
            backgroundColor: isDark ? '#262626' : '#f4f4f4',
            borderRadius: '4px'
          }}>
            <p style={{ fontSize: '0.875rem', color: textSecondary }}>
              <strong>Tip:</strong> Gateway sources (MQTT/HTTP) provide continuous monitoring without requiring your phone to be nearby. 
              App sources require an active Bluetooth connection from your mobile device.
            </p>
          </div>
        </div>
      </Modal>

      {/* AI Analysis Modal */}
      <Modal
        open={aiModalOpen}
        modalHeading="AI Device Analysis"
        passiveModal
        onRequestClose={() => {
          setAiModalOpen(false);
          setAiAnalysis(null);
          setAiError(null);
        }}
        size="lg"
      >
        <div style={{ minHeight: '300px' }}>
          {aiLoading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '48px',
              gap: '16px'
            }}>
              <Loading description="Analyzing your sensor data..." withOverlay={false} />
              <p style={{ color: textSecondary, textAlign: 'center' }}>
                Analyzing {devices.length} devices...
              </p>
              <p style={{ color: textSecondary, fontSize: '0.875rem', textAlign: 'center' }}>
                This may take a few moments
              </p>
            </div>
          ) : aiError ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '48px',
              gap: '16px'
            }}>
              <WarningAltFilled size={64} style={{ color: '#da1e28' }} />
              <p style={{ color: textPrimary, fontWeight: 600, fontSize: '1.125rem' }}>Analysis Failed</p>
              <p style={{ color: textSecondary, textAlign: 'center' }}>{aiError}</p>
              <Button kind="secondary" onClick={handleAIAnalysis}>
                Try Again
              </Button>
            </div>
          ) : aiAnalysis ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Confidence Indicator */}
              {aiAnalysis.confidence && (
                <div style={{ 
                  padding: '12px 16px',
                  backgroundColor: isDark ? '#262626' : '#f4f4f4',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Analytics size={20} style={{ color: '#0f62fe' }} />
                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>
                    Confidence: {Math.round(aiAnalysis.confidence * 100)}%
                  </span>
                  {aiAnalysis.cached && (
                    <Tag type="cool-gray" size="sm">Cached Response</Tag>
                  )}
                </div>
              )}

              {/* Intelligence contexts used */}
              {(aiAnalysis.contextsUsed?.length > 0 || aiAnalysis.devicesWithoutContext?.length > 0) && (
                <div>
                  <h4 style={{
                    color: textPrimary,
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <Information size={20} style={{ color: '#8a3ffc' }} />
                    Intelligence Context
                  </h4>
                  {aiAnalysis.contextsUsed?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                      {aiAnalysis.contextsUsed.map((ctx, index) => (
                        <div
                          key={`${ctx.deviceName}-${index}`}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: isDark ? '#262626' : '#f4f4f4',
                            borderRadius: '6px',
                            borderLeft: `3px solid ${ctx.critical ? '#da1e28' : '#24a148'}`,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span>{ctx.contextIcon}</span>
                            <strong style={{ color: textPrimary }}>{ctx.deviceName}</strong>
                            <Tag type={ctx.critical ? 'red' : 'green'} size="sm">{ctx.contextLabel}</Tag>
                          </div>
                          <p style={{ color: textSecondary, fontSize: '0.8125rem', margin: 0 }}>
                            Expected: {ctx.tempRange}
                            {ctx.temperature != null ? ` · Reading: ${Number(ctx.temperature).toFixed(1)}°C` : ''}
                            {ctx.status ? ` · ${ctx.status}` : ''}
                          </p>
                          {ctx.customNotes && (
                            <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '6px 0 0' }}>{ctx.customNotes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {aiAnalysis.devicesWithoutContext?.length > 0 && (
                    <p style={{ color: textSecondary, fontSize: '0.8125rem', margin: 0 }}>
                      No context assigned: {aiAnalysis.devicesWithoutContext.join(', ')}.
                      Assign context on the Intelligence page for richer analysis.
                    </p>
                  )}
                </div>
              )}

              {/* Main Analysis */}
              <div>
                <h4 style={{ 
                  color: textPrimary, 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Analytics size={20} style={{ color: '#0f62fe' }} />
                  Analysis Summary
                </h4>
                <p style={{ 
                  color: textSecondary, 
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {aiAnalysis.analysis}
                </p>
              </div>

              {/* Insights */}
              {aiAnalysis.insights && aiAnalysis.insights.length > 0 && (
                <div>
                  <h4 style={{ 
                    color: textPrimary, 
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <LightFilled size={20} style={{ color: '#f1c21b' }} />
                    Key Insights
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '0',
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {aiAnalysis.insights.map((insight, index) => (
                      <li 
                        key={index}
                        style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: isDark ? '#262626' : '#f4f4f4',
                          borderRadius: '4px'
                        }}
                      >
                        <LightFilled size={16} style={{ color: '#f1c21b', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ color: textSecondary }}>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
                <div>
                  <h4 style={{ 
                    color: textPrimary, 
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Checkmark size={20} style={{ color: '#24a148' }} />
                    Recommendations
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '0',
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {aiAnalysis.recommendations.map((rec, index) => (
                      <li 
                        key={index}
                        style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: isDark ? '#262626' : '#f4f4f4',
                          borderRadius: '4px'
                        }}
                      >
                        <Checkmark size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ color: textSecondary }}>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Notification */}
      {notification && (
        <div style={{ 
          position: 'fixed', 
          top: '80px', 
          right: '16px', 
          zIndex: 9999,
          maxWidth: '400px'
        }}>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      <style>{`
        /* Mobile responsive styles for device cards */
        @media (max-width: 671px) {
          /* Ensure cards fit within viewport */
          [data-testid^="device-card-"] {
            margin-left: 0 !important;
            margin-right: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }

          /* Ensure card content has proper padding */
          [data-testid^="device-card-"] > div:first-child {
            padding: 12px !important;
          }

          /* Ensure action buttons stay horizontal on mobile */
          [data-testid^="device-card-"] > div:last-child {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            justify-content: flex-end !important;
            gap: 6px !important;
            padding: 12px !important;
          }

          /* Make buttons more compact on mobile */
          [data-testid^="device-card-"] > div:last-child button {
            padding: 6px 12px !important;
            font-size: 0.75rem !important;
            min-height: 32px !important;
            white-space: nowrap !important;
          }

          /* Adjust header for mobile */
          [data-testid="device-list-page"] h1 {
            font-size: 1.5rem !important;
          }

          /* Make stats grid fit properly on mobile - ensure all columns visible */
          [data-testid^="device-card-"] > div:nth-child(2) {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 0 !important;
            width: 100% !important;
            overflow: visible !important;
          }

          /* Ensure proper padding for all stat columns, especially the last one */
          [data-testid^="device-card-"] > div:nth-child(2) > div {
            padding: 12px 6px !important;
            min-width: 0 !important;
            overflow: visible !important;
          }

          /* Ensure last column (Battery) has proper right padding */
          [data-testid^="device-card-"] > div:nth-child(2) > div:last-child {
            padding-right: 12px !important;
          }

          /* Ensure first column has proper left padding */
          [data-testid^="device-card-"] > div:nth-child(2) > div:first-child {
            padding-left: 12px !important;
          }

          [data-testid^="device-card-"] > div:nth-child(2) p:last-child {
            font-size: 1rem !important;
          }

          /* Ensure icons don't get cut off */
          [data-testid^="device-card-"] > div:nth-child(2) svg {
            flex-shrink: 0 !important;
            min-width: 16px !important;
          }
        }

        @media (max-width: 480px) {
          /* Extra compact for very small screens */
          [data-testid^="device-card-"] > div:first-child {
            padding: 10px !important;
          }

          /* Extra compact buttons for very small screens */
          [data-testid^="device-card-"] > div:last-child {
            gap: 4px !important;
            padding: 10px !important;
          }

          [data-testid^="device-card-"] > div:last-child button {
            padding: 6px 10px !important;
            font-size: 0.6875rem !important;
            min-height: 28px !important;
          }

          /* More compact stats on very small screens */
          [data-testid^="device-card-"] > div:nth-child(2) > div {
            padding: 10px 4px !important;
          }

          [data-testid^="device-card-"] > div:nth-child(2) > div:last-child {
            padding-right: 10px !important;
          }

          [data-testid^="device-card-"] > div:nth-child(2) > div:first-child {
            padding-left: 10px !important;
          }

          [data-testid^="device-card-"] > div:nth-child(2) p:last-child {
            font-size: 0.9375rem !important;
          }
        }
      `}</style>
      {/* Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div>
                    <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, marginBottom: '4px' }}>Devices</h1>
                    <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{filteredDevices.length} of {devices.length} devices</p>
                  </div>
                  <Tooltip align="bottom" label="Data source information">
                    <button
                      onClick={() => setSourceInfoModalOpen(true)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: textSecondary
                      }}
                    >
                      <Information size={20} />
                    </button>
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px', width: '150px' }}>
                  <Button kind="primary" renderIcon={Add} onClick={() => navigate('/dashboard/devices/add')} data-testid="add-device" style={{ whiteSpace: 'nowrap', width: '100%' }}>
                    Add Device
                  </Button>
                  <Button 
                    kind="primary" 
                    renderIcon={Analytics} 
                    onClick={handleAIAnalysis}
                    data-testid="ai-analysis"
                    style={{ 
                      whiteSpace: 'nowrap',
                      width: '100%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      border: 'none'
                    }}
                  >
                    AI Analysis
                  </Button>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                  <Search
                    labelText="Search"
                    placeholder="Search devices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="lg"
                    data-testid="search-devices"
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={GridIcon}
                    iconDescription="Card view"
                    onClick={() => setViewMode('cards')}
                  />
                  <Button
                    kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={List}
                    iconDescription="Table view"
                    onClick={() => setViewMode('table')}
                  />
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={Renew}
                    iconDescription="Refresh data"
                    onClick={() => {
                      refetch();
                    }}
                  />
                </div>
                <div style={{ minWidth: '200px', flexShrink: 0, marginLeft: 'auto' }}>
                  <Dropdown
                    id="status-filter"
                    titleText="Filter by Status"
                    label="Select status"
                    items={['All Devices', 'Online', 'Warning', 'Offline']}
                    selectedItem={statusFilter === 'all' ? 'All Devices' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) {
                        // Map display text back to status value
                        const statusMap = {
                          'All Devices': 'all',
                          'Online': 'online',
                          'Warning': 'warning',
                          'Offline': 'offline',
                        };
                        setStatusFilter(statusMap[selectedItem] || 'all');
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          {filteredDevices.length === 0 ? (
            <Column lg={16} md={8} sm={4}>
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <p style={{ color: textSecondary, fontSize: '1rem', marginBottom: 0 }}>
                  {searchTerm
                    ? 'No sensor devices found matching your search.'
                    : 'No sensor devices found. Add your first sensor to get started.'}
                </p>
              </div>
            </Column>
          ) : viewMode === 'cards' ? (
            // Card View
            filteredDevices.map((device) => (
              <Column lg={8} md={4} sm={4} key={device.id}>
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    borderLeft: `4px solid ${getStatusColor(device.status)}`,
                    padding: '0',
                    marginBottom: '16px',
                    overflow: 'visible',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  data-testid={`device-card-${device.id}`}
                >
                  {/* Card Header */}
                  <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getStatusIcon(device.status)}
                        <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                          {device.name}
                        </h3>
                      </div>
                      <Tag type="blue" size="sm">{device.type}</Tag>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textSecondary, fontSize: '0.75rem' }}>
                      <span>{device.zone}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textSecondary, fontSize: '0.75rem', marginTop: '4px' }}>
                      <span style={{ fontWeight: 600 }}>LAST READING:</span>
                      <span>{device.lastReading}</span>
                      {device.source && (
                        <>
                          <span>•</span>
                          {/* Show actual source as text tag */}
                          {device.source === 'mqtt' ? (
                            <Tag type="purple" size="sm">MQTT</Tag>
                          ) : device.source === 'http' ? (
                            <Tag type="cyan" size="sm">HTTP</Tag>
                          ) : device.source === 'app' ? (
                            <Tag type="blue" size="sm">APP</Tag>
                          ) : (
                            <Tag type="gray" size="sm">{device.source.toUpperCase()}</Tag>
                          )}
                          {/* Show LIVE indicator if actively receiving data */}
                          {device.isLive && (
                            <Tag type="red" size="sm">LIVE</Tag>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Card Body - Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: `1px solid ${borderColor}`, width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ padding: '16px', textAlign: 'center', borderRight: `1px solid ${borderColor}`, minWidth: 0, overflow: 'visible' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                        <Temperature size={16} style={{ color: '#0f62fe', flexShrink: 0 }} />
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Temp</p>
                      <p style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, margin: 0, wordBreak: 'break-word' }}>{device.temp}</p>
                      <SensorStatusIndicator 
                        status={device.temperatureStatus} 
                        type="temperature" 
                        showLabel={false}
                        className={isDark ? 'dark' : ''}
                      />
                    </div>
                    <div style={{ padding: '16px', textAlign: 'center', borderRight: `1px solid ${borderColor}`, minWidth: 0, overflow: 'visible' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                        <Humidity size={16} style={{ color: '#6929c4', flexShrink: 0 }} />
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Humidity</p>
                      <p style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, margin: 0, wordBreak: 'break-word' }}>{device.humidity}</p>
                      <SensorStatusIndicator 
                        status={device.humidityStatus} 
                        type="humidity" 
                        showLabel={false}
                        className={isDark ? 'dark' : ''}
                      />
                    </div>
                    <div style={{ padding: '16px', textAlign: 'center', minWidth: 0, overflow: 'visible' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                        <BatteryFull size={16} style={{ color: getBatteryColor(device.batteryPct), flexShrink: 0 }} />
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '2px' }}>Battery</p>
                      <p style={{ color: getBatteryColor(device.batteryPct), fontSize: '1.25rem', fontWeight: 600, margin: 0, wordBreak: 'break-word' }}>{device.battery}</p>
                    </div>
                  </div>

                  {/* Card Footer - Actions */}
                  <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button kind="ghost" size="sm" renderIcon={View} onClick={() => navigate(`/dashboard/devices/${device.id}`)} data-testid={`view-${device.id}`}>View</Button>
                    <Button kind="danger--ghost" size="sm" renderIcon={TrashCan} onClick={() => handleDeleteClick(device)} data-testid={`delete-${device.id}`}>Delete</Button>
                  </div>
                </Tile>
              </Column>
            ))
          ) : (
            // Table View
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Device</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Zone</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Temp</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Humidity</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Battery</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Last Reading</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDevices.map((device, idx) => (
                      <tr
                        key={device.id}
                        style={{
                          borderBottom: `1px solid ${borderColor}`,
                          backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                        }}
                        data-testid={`device-row-${device.id}`}
                      >
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {getStatusIcon(device.status)}
                            <span style={{ color: getStatusColor(device.status), fontSize: '0.875rem', textTransform: 'capitalize' }}>
                              {device.status}
                            </span>
                            {device.status === 'warning' && (
                              <Tooltip
                                align="bottom"
                                label={
                                  <div style={{ maxWidth: '250px', padding: '4px' }}>
                                    <strong>Warning Status</strong>
                                    <br />
                                    <br />
                                    No readings received in the last 15-60 minutes. This may be normal if your sync settings are configured for periodic updates.
                                    <br />
                                    <br />
                                    <strong>To refresh data:</strong> Use the mobile app to manually sync the device, or check device range, battery level, or connectivity if sync settings allow more frequent updates.
                                  </div>
                                }
                              >
                                <button
                                  type="button"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: textSecondary,
                                  }}
                                  aria-label="Warning status information"
                                >
                                  <Information size={14} />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div>
                            <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '2px' }}>{device.name}</p>
                            <Tag type="blue" size="sm">{device.type}</Tag>
                          </div>
                        </td>
                        <td style={{ padding: '16px', color: textSecondary }}>{device.zone}</td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{ color: '#0f62fe', fontWeight: 600, marginBottom: '2px' }}>{device.temp}</div>
                          <SensorStatusIndicator 
                            status={device.temperatureStatus} 
                            type="temperature" 
                            showLabel={false}
                            className={isDark ? 'dark' : ''}
                          />
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{ color: '#6929c4', fontWeight: 600, marginBottom: '2px' }}>{device.humidity}</div>
                          <SensorStatusIndicator 
                            status={device.humidityStatus} 
                            type="humidity" 
                            showLabel={false}
                            className={isDark ? 'dark' : ''}
                          />
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: getBatteryColor(device.batteryPct), fontWeight: 600 }}>{device.battery}</span>
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem' }}>{device.lastReading}</td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="View" onClick={() => navigate(`/dashboard/devices/${device.id}`)} />
                            <Button kind="danger--ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Delete" onClick={() => handleDeleteClick(device)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination - only show for table view */}
              {totalItems > 0 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={page}
                    pageNumberText="Page Number"
                    pageSize={pageSize}
                    pageSizes={[10, 20, 30, 40, 50]}
                    size="md"
                    totalItems={totalItems}
                    onChange={({ page, pageSize }) => {
                      setPage(page);
                      setPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </Column>
          )}
        </Grid>
      </div>
    </div>
    </SubscriptionGate>
  );
};

export default DeviceListPage;
